import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import TxnTypeBadge from "@/components/TxnTypeBadge";

/**
 * StatementLedger — running-balance ledger for a single statement.
 *
 * Fetches `/api/statements/:id/ledger` and renders:
 *   1. Opening balance row
 *   2. One row per transaction with a running balance column
 *   3. A subtotal row showing computed running vs stated closing
 *   4. Closing balance row (with expected-vs-actual variance chip)
 *   5. Category rollup mini-table (Debits only)
 *   6. Missing-in-VAULT count + per-row flag
 *
 * Works in two modes:
 *   - `variant="compact"` — inside the Statement Review dialog
 *   - `variant="page"` — full width, print-friendly, on the dedicated page
 */
export default function StatementLedger({ statementId, variant = "compact" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!statementId) return;
        setLoading(true);
        api.get(`/statements/${statementId}/ledger`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Failed to load ledger"))
            .finally(() => setLoading(false));
    }, [statementId]);

    if (loading) return <div className="p-6 text-stone-500 flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Building ledger…</div>;
    if (error)   return <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded" data-testid="ledger-error">{error}</div>;
    if (!data)   return null;

    const isPage = variant === "page";
    const varianceOk = Math.abs(data.variance) < 1;

    return (
        <div className="space-y-4" data-testid="statement-ledger">
            {/* Header meta */}
            <div className={`grid gap-3 ${isPage ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-4"}`}>
                <MetaCell label="Opening Balance" value={fmtMoney(data.opening_balance)} testid="ledger-opening" />
                <MetaCell label="Closing Balance" value={fmtMoney(data.closing_balance)} testid="ledger-closing" />
                <MetaCell label="Total Amount Due" value={fmtMoney(data.total_amount_due)} />
                <MetaCell label="Min Amount Due" value={fmtMoney(data.min_amount_due)} />
            </div>

            {/* Variance check */}
            <div className={`px-3 py-2 border rounded-md text-xs flex items-center justify-between ${varianceOk ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="ledger-variance">
                <div className="flex items-center gap-2">
                    {varianceOk ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                    <span>
                        <strong>{varianceOk ? "Math checks out." : "Variance found."}</strong>{" "}
                        Expected closing = Opening + Purchases + Interest + Fees + GST − Payments
                        {" "}= <span className="font-mono-figure">{fmtMoney(data.expected_closing)}</span>
                        {" "}· Statement closing = <span className="font-mono-figure">{fmtMoney(data.closing_balance)}</span>
                    </span>
                </div>
                <span className="font-mono-figure">Δ {fmtMoney(data.variance)}</span>
            </div>

            {/* Ledger table */}
            <div className={`border border-stone-200 rounded-md ${isPage ? "" : "max-h-[420px] overflow-y-auto"}`}>
                <table className="dense-table w-full">
                    <thead className="sticky top-0 bg-stone-50 z-[1]">
                        <tr>
                            <th className="w-12">#</th>
                            <th>Date</th>
                            <th>Merchant</th>
                            <th>Type</th>
                            <th className="text-right">Amount</th>
                            <th className="text-right">Running Balance</th>
                            <th className="text-center">In VAULT?</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="bg-stone-50 border-b-2 border-stone-200 font-medium" data-testid="ledger-row-opening">
                            <td>—</td>
                            <td className="tabular-nums text-stone-500">{fmtDate(data.period_from) || "—"}</td>
                            <td className="uppercase text-xs tracking-widest text-stone-500">Opening Balance</td>
                            <td>—</td>
                            <td className="text-right">—</td>
                            <td className="text-right tabular-nums">{fmtMoney(data.opening_balance)}</td>
                            <td>—</td>
                        </tr>
                        {data.rows.length === 0 && (
                            <tr><td colSpan={7} className="text-center text-stone-500 py-6">No transactions parsed from this statement.</td></tr>
                        )}
                        {data.rows.map((r) => {
                            const isCredit = r.type === "Credit";
                            return (
                                <tr key={r.index} data-testid={`ledger-row-${r.index}`}>
                                    <td className="text-stone-400 tabular-nums text-[11px]">{r.index + 1}</td>
                                    <td className="tabular-nums">{r.date || "—"}</td>
                                    <td>{r.merchant || "—"}</td>
                                    <td><TxnTypeBadge value={r.type} /></td>
                                    <td className={`text-right tabular-nums ${isCredit ? "text-emerald-700 font-medium" : ""}`}>
                                        {isCredit ? "+ " : "− "}{fmtMoney(r.amount)}
                                    </td>
                                    <td className="text-right tabular-nums font-medium">{fmtMoney(r.running_balance)}</td>
                                    <td className="text-center">
                                        {r.missing_in_vault
                                            ? <span className="text-[10px] px-1.5 py-0.5 border border-amber-200 bg-amber-50 text-amber-800 rounded font-medium tracking-wide uppercase" data-testid={`ledger-missing-${r.index}`}>Missing</span>
                                            : <CheckCircle2 size={12} className="inline text-emerald-600" aria-label="in vault" />
                                        }
                                    </td>
                                </tr>
                            );
                        })}
                        <tr className="bg-stone-50 border-t-2 border-stone-200 font-medium" data-testid="ledger-row-closing">
                            <td>—</td>
                            <td className="tabular-nums text-stone-500">{fmtDate(data.period_to) || fmtDate(data.statement_date) || "—"}</td>
                            <td className="uppercase text-xs tracking-widest text-stone-500">Closing Balance</td>
                            <td>—</td>
                            <td className="text-right">—</td>
                            <td className="text-right tabular-nums">{fmtMoney(data.closing_balance)}</td>
                            <td>—</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Category rollup */}
            {data.category_rollup?.length > 0 && (
                <div className="border border-stone-200 rounded-md">
                    <div className="px-3 py-2 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
                        <div className="eyebrow">Category rollup · debits only</div>
                        <div className="text-[11px] text-stone-500">
                            {data.missing_count > 0
                                ? <span className="text-amber-700"><strong>{data.missing_count}</strong> of {data.txn_count} txns missing in VAULT</span>
                                : <span className="text-emerald-700">All {data.txn_count} txns matched in VAULT</span>}
                        </div>
                    </div>
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th className="text-right">Txns</th>
                                <th className="text-right">Total</th>
                                <th className="text-right w-40">% of Purchases</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.category_rollup.map((c) => {
                                const pct = data.purchases > 0 ? (c.total / data.purchases) * 100 : 0;
                                return (
                                    <tr key={c.category} data-testid={`ledger-cat-${c.category}`}>
                                        <td>{c.category}</td>
                                        <td className="text-right tabular-nums">{c.count}</td>
                                        <td className="text-right tabular-nums font-medium">{fmtMoney(c.total)}</td>
                                        <td className="text-right tabular-nums text-stone-500">{pct.toFixed(1)}%</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function MetaCell({ label, value, testid }) {
    return (
        <div className="border border-stone-200 rounded-md px-3 py-2 bg-white">
            <div className="eyebrow">{label}</div>
            <div className="text-lg font-medium tabular-nums mt-0.5" data-testid={testid}>{value}</div>
        </div>
    );
}
